import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { DodoPayments } from 'dodopayments-checkout';
import { billingAPI } from '../../api/client';
import {
    trackLandingPageView,
    trackHeroCtaClick,
    trackStartTestClick,
    trackLandingNavClick,
    trackHowItWorksView,
    trackPricingSectionView,
    trackComparisonSectionView,
    trackCtaSectionView,
    trackFinalCtaClick,
    trackFooterLinkClick,
    trackPricingBuyClick,
    trackCheckoutOpened,
    trackCheckoutSuccess,
    trackCheckoutError,
} from '../../utils/analytics';
import './LandingPage.css';

const faqData = [
    {
        question: 'What is ALT and how does it work?',
        answer: 'ALT is a cloud-based, no-code API load testing platform. You paste a cURL command, import a Postman collection, or fill in your API details — then choose a load profile (Fixed, Ramp Up, Spike, or Peak) and hit run. You get real p90/p95/p99 latency metrics, throughput, and error rates within 60 seconds. No scripts, no infrastructure, no installation required.',
    },
    {
        question: 'Do I need to write code or install anything?',
        answer: 'No. ALT is entirely browser-based with a no-code interface. You don\'t need to write JavaScript (like k6), Scala (like Gatling), or create XML test plans (like JMeter). Just paste your cURL or import from Postman and you\'re ready to run API load tests. There is also an API if you want to automate tests in CI/CD pipelines.',
    },
    {
        question: 'How many virtual users and how much traffic can I simulate?',
        answer: 'You can configure up to 100 virtual users (concurrent connections) per test with durations ranging from 10 to 60 seconds. ALT offers 4 load profiles — Fixed, Ramp Up, Spike, and Peak — to simulate different real-world traffic patterns against your API.',
    },
    {
        question: 'How does pricing work?',
        answer: 'ALT uses a pay-as-you-go credit pack model — no subscriptions. You purchase credit packs on a one-time basis, and 1 credit = 1 test run. Packs start at $4.99 for 5 credits and go up to $199.99 for 200 credits. Credits never expire, so you can use them whenever you need.',
    },
    {
        question: 'Can I integrate ALT into my CI/CD pipeline?',
        answer: 'Absolutely. ALT provides a REST API that lets you trigger API load tests with a single cURL call. You can plug this into GitHub Actions, GitLab CI, Jenkins, or any CI/CD system. The API returns JSON with full p90/p95/p99 metrics so you can set up automated performance gates on every deploy.',
    },
    {
        question: 'What metrics do I get from a load test?',
        answer: 'Every test run gives you: total requests, throughput (requests per second), average/min/max response times, p90/p95/p99 percentile response times, error rate, and status code breakdown. These are industry-standard API load testing metrics that help you find bottlenecks and tail latency issues before they impact your users at scale.',
    },
];

const LandingPage = () => {
    const navigate = useNavigate();
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    const [checkoutLoading, setCheckoutLoading] = useState(null);
    const [openFaq, setOpenFaq] = useState(null);
    const [videoSrc, setVideoSrc] = useState('https://www.youtube.com/embed/XiFf4U734CY');

    const heroRef = useRef(null);
    const howItWorksRef = useRef(null);
    const pricingRef = useRef(null);
    const comparisonRef = useRef(null);
    const ctaSectionRef = useRef(null);

    useEffect(() => {
        const timer = setTimeout(() => {
            setVideoSrc('https://www.youtube.com/embed/XiFf4U734CY?autoplay=1');
        }, 3000);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => { trackLandingPageView(); }, []);

    useEffect(() => {
        const firedSections = new Set();
        const sectionMap = [
            { ref: howItWorksRef, track: trackHowItWorksView, key: 'how_it_works' },
            { ref: pricingRef, track: trackPricingSectionView, key: 'pricing' },
            { ref: comparisonRef, track: trackComparisonSectionView, key: 'comparison' },
            { ref: ctaSectionRef, track: trackCtaSectionView, key: 'cta' },
        ];
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const section = sectionMap.find(s => s.ref.current === entry.target);
                        if (section && !firedSections.has(section.key)) {
                            firedSections.add(section.key);
                            section.track();
                        }
                    }
                });
            },
            { threshold: 0.3 }
        );
        sectionMap.forEach(({ ref }) => { if (ref.current) observer.observe(ref.current); });
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        DodoPayments.Initialize({
            mode: process.env.REACT_APP_DODO_MODE === 'live' ? 'live' : 'test',
            displayType: 'overlay',
            onEvent: (event) => {
                if (event.event_type === 'checkout.status') {
                    const status = event.data?.message?.status;
                    if (status === 'succeeded') {
                        trackCheckoutSuccess('unknown', 'unknown', 0);
                        navigate('/login');
                    }
                }
                if (event.event_type === 'checkout.redirect_requested') {
                    const redirectTo = event.data?.message?.redirect_to;
                    if (redirectTo) window.location.href = redirectTo;
                }
                if (event.event_type === 'checkout.error') {
                    trackCheckoutError('unknown', event.data?.message || 'checkout_error');
                }
            },
        });
    }, [navigate]);

    const handleCheckout = useCallback(async (tierId) => {
        if (checkoutLoading) return;
        setCheckoutLoading(tierId);
        try {
            const response = await billingAPI.createCheckoutSession(tierId);
            if (response.data?.checkoutUrl) {
                trackCheckoutOpened(tierId, tierId, 0);
                DodoPayments.Checkout.open({
                    checkoutUrl: response.data.checkoutUrl,
                    options: { manualRedirect: true },
                });
            } else {
                alert('Failed to start checkout. Please try again.');
            }
        } catch (err) {
            console.error('Checkout error:', err);
            trackCheckoutError(tierId, err.message || 'checkout_start_failed');
            alert('Failed to start checkout. Please try again.');
        } finally {
            setCheckoutLoading(null);
        }
    }, [checkoutLoading]);

    const navClick = (section) => { trackLandingNavClick(section); setMobileNavOpen(false); };

    return (
        <div className="landing-page-wrapper">

            <Helmet>
                <title>APILoadTest.com - JMeter Alternative | No-Code API Load Testing Tool</title>
                <meta name="description" content="No-code API load testing tool. Test REST APIs and get p90/p95/p99 metrics. Import Postman collections." />
                <meta name="keywords" content="JMeter alternative, k6 alternative, Gatling alternative, no-code load testing, API load testing, Postman load testing, API performance testing, load test API, REST API testing, no-code API testing, cloud load testing" />
                <meta property="og:title" content="APILoadTest.com - JMeter Alternative | No-Code API Load Testing" />
                <meta property="og:description" content="The easiest JMeter & k6 alternative. No scripting, no infrastructure. Test APIs in 60 seconds with p90/p95/p99 metrics." />
                <meta property="og:url" content="https://www.apiloadtest.com" />
                <meta property="og:type" content="website" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="APILoadTest.com - JMeter Alternative | No-Code Load Testing" />
                <meta name="twitter:description" content="Skip JMeter's complexity. Test APIs in 60 seconds with no scripting." />
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "SoftwareApplication",
                        "name": "APILoadTest.com",
                        "alternateName": ["JMeter Alternative", "k6 Alternative", "Gatling Alternative"],
                        "url": "https://www.apiloadtest.com",
                        "description": "No-code API load testing tool. The easiest alternative to JMeter, k6, and Gatling for testing API performance.",
                        "applicationCategory": "DeveloperApplication",
                        "operatingSystem": "Web",
                        "offers": { "@type": "Offer", "price": "4.99", "priceCurrency": "USD", "description": "Credit packs starting at $4.99, no subscription required" },
                        "featureList": [
                            "No-code API load testing", "Postman collection import", "cURL command import",
                            "p90/p95/p99 response time metrics",
                            "4 load profiles (Fixed, Ramp Up, Spike, Peak)", "Real-time results",
                            "Bearer token and API key authentication"
                        ]
                    })}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "FAQPage",
                        "mainEntity": faqData.map(faq => ({
                            "@type": "Question",
                            "name": faq.question,
                            "acceptedAnswer": {
                                "@type": "Answer",
                                "text": faq.answer,
                            },
                        })),
                    })}
                </script>
            </Helmet>

            {/* ── NAV ── */}
            <nav className="lp-nav">
                <a className="logo" href="/">
                    <img src="/logo.png" alt="APILoadTest" className="logo-img" />
                    APILoadTest.com
                </a>
                <div className="nav-links">
                    <a href="#features" onClick={() => navClick('features')}>Features</a>
                    <a href="#how-it-works" onClick={() => navClick('how_it_works')}>How It Works</a>
                    <a href="#pricing" onClick={() => navClick('pricing')}>Pricing</a>
                    <a href="/blog" onClick={() => navClick('blog')}>Blog</a>
                </div>
                <button className="nav-cta desktop-only" onClick={() => { trackStartTestClick('header'); navigate('/login'); }}>
                    Get Started →
                </button>
                <button className="mobile-nav-toggle" onClick={() => setMobileNavOpen(!mobileNavOpen)}>
                    {mobileNavOpen ? '✕' : '☰'}
                </button>
                {mobileNavOpen && (
                    <div className="mobile-nav-dropdown">
                        <a href="#features" onClick={() => navClick('features')}>Features</a>
                        <a href="#how-it-works" onClick={() => navClick('how_it_works')}>How It Works</a>
                        <a href="#pricing" onClick={() => navClick('pricing')}>Pricing</a>
                        <a href="/blog" onClick={() => navClick('blog')}>Blog</a>
                        <button className="nav-cta" onClick={() => { trackStartTestClick('mobile_nav'); navigate('/login'); setMobileNavOpen(false); }}>
                            Get Started →
                        </button>
                    </div>
                )}
            </nav>

            {/* ── HERO ── */}
            <div className="hero hero--single" ref={heroRef}>
                <div className="hero-left">
                    <div className="hero-badge">
                        <span className="badge-dot"></span>
                        No scripting required
                    </div>
                    <h1>Instant Load Testing {' '}<br />for <em>Modern APIs</em></h1>
                    <p className="hero-sub">
                        Don't wait for a production outage. Simulate your user traffic with ALT and get a
                        reality check — without the config/setup mess of JMeter, k6 or Gatling. <br />Just paste a cURL
                        or import Postman, and get real p90/p95/p99 latency data
                        in under 60 seconds.
                    </p>
                    <p className="hero-sub">
                        Pay as you go — <em>from $0.99 per test</em>.
                    </p>
                    <div className="hero-actions">
                        <button className="lp-btn-primary" onClick={() => { trackHeroCtaClick(); navigate('/login'); }}>
                            Find Your API's Breaking Point →
                        </button>
                    </div>
                    <div className="hero-trust">
                        ⏱️ Run your first load test in under 60 seconds
                    </div>
                </div>
            </div>

            {/* ── ANSWER-FIRST DEFINITION ── */}
            <section className="lp-section answer-first-section" id="what-is-apiloadtest">
                <div className="section-inner">
                    <div className="answer-first-grid">
                        <div className="answer-first-video">
                            <div className="video-wrapper">
                                <iframe
                                    src={videoSrc}
                                    title="ALT Tutorial - API Load Testing"
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                ></iframe>
                            </div>
                        </div>
                        <div className="answer-first-block">
                            <h2>What is APILoadTest?</h2>
                            <p className="answer-first-text">
                                <strong>APILoadTest</strong> is a cloud-based, no-code API load testing platform. It lets developers, QA engineers, and DevOps teams test how REST APIs perform under real-world traffic — without writing scripts, managing infrastructure, or installing software. Paste a cURL command or import a Postman collection, choose a load profile, and get p90/p95/p99 latency metrics in under 60 seconds. It's the easiest alternative to JMeter, k6, and Gatling.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── TWO WAYS TO TEST ── */}
            <section className="lp-section use-cases-section" id="use-cases">
                <div className="section-inner">
                    <div className="section-eyebrow">Two ways to test</div>
                    <h2>Choose your {' '}<br /><em>workflow.</em></h2>
                    <p className="section-sub">Whether you prefer a visual interface or programmatic control — we've got you covered.</p>
                    <div className="use-cases-grid">
                        {/* PRIMARY – No-Code GUI */}
                        <div className="use-case-card use-case-primary">
                            <div className="use-case-icon-row">
                                <span className="use-case-icon">🖥️</span>
                            </div>
                            <div className="use-case-eyebrow">No-Code Dashboard</div>
                            <h3>Point, click, <em>test.</em></h3>
                            <p className="use-case-desc">
                                Import a cURL command, a Postman collection, or simply fill in your API details — then pick a load profile and hit run. No scripting, no config files, no CLI.
                            </p>
                            <ul className="use-case-list">
                                <li><span className="uc-check">✓</span> Import cURL or Postman in one click</li>
                                <li><span className="uc-check">✓</span> Visual load profile selector</li>
                                <li><span className="uc-check">✓</span> Real-time results dashboard</li>
                                <li><span className="uc-check">✓</span> No scripting or installation</li>
                            </ul>
                            <button className="lp-btn-primary use-case-cta" onClick={() => { trackStartTestClick('use_case_gui'); navigate('/login'); }}>
                                Try the Dashboard →
                            </button>
                        </div>

                        {/* SECONDARY – API / CI/CD */}
                        <div className="use-case-card use-case-secondary">
                            <div className="use-case-icon-row">
                                <span className="use-case-icon">⚡</span>
                            </div>
                            <div className="use-case-eyebrow">API &amp; CI/CD</div>
                            <h3>Automate with a <em>single cURL.</em></h3>
                            <p className="use-case-desc">
                                Trigger load tests straight from your CI/CD pipeline — GitHub Actions, GitLab CI, Jenkins, or any workflow. One cURL call, real results, every deploy.
                            </p>
                            <div className="snippet-card">
                                <div className="snippet-header">
                                    <span className="snippet-lang">cURL</span>
                                    <span className="snippet-label">Run via API</span>
                                </div>
                                <pre className="snippet-code"><code>{`curl -X POST https://api.apiloadtest.com/api/api-test/run \\
  -H "Authorization: Bearer alt_live_YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "url": "https://api.example.com/v1/users",
    "method": "GET",
    "connections": 50,
    "duration": 30
  }'`}</code></pre>
                            </div>
                            <ul className="use-case-list">
                                <li><span className="uc-check">✓</span> Works with any CI/CD tool</li>
                                <li><span className="uc-check">✓</span> JSON response with p90/p95/p99</li>
                                <li><span className="uc-check">✓</span> Catch regressions on every deploy</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── FEATURES ── */}
            <section className="lp-section" id="features">
                <div className="section-inner">
                    <div className="section-eyebrow">Features</div>
                    <h2>Everything you need. {' '}<br /><em>Zero</em> setup friction.</h2>
                    <p className="section-sub">Find bottlenecks before your users do — built for developers who value their time.</p>
                    <div className="features-grid">
                        <div className="feat">
                            <div className="feat-icon blue">🚀</div>
                            <h3>No-Code Test Builder</h3>
                            <p>Design load test scenarios with a visual editor. Set virtual users, choose load profiles, configure headers and auth — no YAML, no scripts.</p>
                        </div>
                        <div className="feat">
                            <div className="feat-icon green">📊</div>
                            <h3>p90 / p95 / p99 Metrics</h3>
                            <p>Industry-standard percentile breakdowns in real time. Instantly spot tail latency before it impacts user experience at scale.</p>
                        </div>
                        <div className="feat">
                            <div className="feat-icon blue">📦</div>
                            <h3>Postman &amp; cURL Import</h3>
                            <p>Drop in your existing Postman collection or paste a cURL command. We'll auto-generate a load test — ready in seconds, not hours.</p>
                        </div>
                        <div className="feat">
                            <div className="feat-icon green">📈</div>
                            <h3>4 Load Profiles</h3>
                            <p>Simulate real-world traffic patterns: Fixed, Ramp Up, Spike, and Peak. Each profile is designed for a specific testing scenario.</p>
                        </div>
                        <div className="feat">
                            <div className="feat-icon orange">🛡️</div>
                            <h3>Auth &amp; Headers Support</h3>
                            <p>Full support for Bearer tokens, API keys, and custom headers. Test secured endpoints exactly as your users would access them.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── HOW IT WORKS ── */}
            <section className="lp-section how-section" id="how-it-works" ref={howItWorksRef}>
                <div className="section-inner">
                    <div className="how-grid">
                        <div>
                            <div className="section-eyebrow">How it works</div>
                            <h2>From zero to results {' '}<br />in <em>60 seconds.</em></h2>
                            <p className="section-sub">Skip the scripting. Skip the infrastructure setup. Just paste and test.</p>
                            <div className="steps" style={{ marginTop: '40px' }}>
                                <div className="step">
                                    <div className="step-num">01</div>
                                    <div className="step-body">
                                        <h3>Import in Seconds</h3>
                                        <p>Paste a cURL or import a Postman collection. URL, headers, auth, and body are automatically configured. No retyping, no manual setup.</p>
                                    </div>
                                </div>
                                <div className="step">
                                    <div className="step-num">02</div>
                                    <div className="step-body">
                                        <h3>Simulate Real Traffic</h3>
                                        <p>Pick a load pattern that matches your use case: steady traffic, gradual ramp-up, sudden spike, or peak hour simulation. No code required.</p>
                                    </div>
                                </div>
                                <div className="step">
                                    <div className="step-num">03</div>
                                    <div className="step-body">
                                        <h3>Find Issues Fast</h3>
                                        <p>Get p90/p95/p99 latencies, error rates, and throughput metrics. Know exactly where your API breaks — before your users find out.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="results-preview">
                            {/* Status Banner */}
                            <div className="rp-status-banner">
                                <div className="rp-status-left">
                                    <span className="rp-status-icon">✓</span>
                                    <div>
                                        <div className="rp-status-label">TEST STATUS</div>
                                        <div className="rp-status-text">All Systems Passed · 100.00% Success Rate</div>
                                    </div>
                                </div>
                                <div className="rp-status-right">34 / 34 requests completed</div>
                            </div>

                            {/* Metrics Cards */}
                            <div className="rp-metrics-row">
                                <div className="rp-metric-card">
                                    <div className="rp-metric-accent blue"></div>
                                    <div className="rp-metric-icon">📦</div>
                                    <div className="rp-metric-label">TOTAL REQUESTS</div>
                                    <div className="rp-metric-value">34<span>reqs</span></div>
                                    <div className="rp-metric-sub">Over 3s window</div>
                                </div>
                                <div className="rp-metric-card">
                                    <div className="rp-metric-accent blue"></div>
                                    <div className="rp-metric-icon">⚡</div>
                                    <div className="rp-metric-label">THROUGHPUT</div>
                                    <div className="rp-metric-value">11.07<span>rps</span></div>
                                    <div className="rp-metric-sub">Requests per second</div>
                                </div>
                                <div className="rp-metric-card">
                                    <div className="rp-metric-accent orange"></div>
                                    <div className="rp-metric-icon">⏱</div>
                                    <div className="rp-metric-label">AVG RESPONSE TIME</div>
                                    <div className="rp-metric-value">257<span>ms</span></div>
                                    <div className="rp-metric-sub">175ms min · 866ms max</div>
                                </div>
                                <div className="rp-metric-card">
                                    <div className="rp-metric-accent green"></div>
                                    <div className="rp-metric-icon">✅</div>
                                    <div className="rp-metric-label">ERROR RATE</div>
                                    <div className="rp-metric-value">0.00<span>%</span></div>
                                    <div className="rp-metric-sub">No failed requests</div>
                                </div>
                            </div>

                            {/* Bottom Row: Response Times + Percentiles */}
                            <div className="rp-bottom-row">
                                <div className="rp-card">
                                    <div className="rp-card-title">⚡ Response Times</div>
                                    <div className="rp-bar-row">
                                        <span className="rp-bar-label">Minimum</span>
                                        <div className="rp-bar-track"><div className="rp-bar-fill blue" style={{ width: '20%' }}></div></div>
                                        <span className="rp-bar-value blue">175 <small>ms</small></span>
                                    </div>
                                    <div className="rp-bar-row">
                                        <span className="rp-bar-label">Average</span>
                                        <div className="rp-bar-track"><div className="rp-bar-fill orange" style={{ width: '45%' }}></div></div>
                                        <span className="rp-bar-value orange">257 <small>ms</small></span>
                                    </div>
                                    <div className="rp-bar-row">
                                        <span className="rp-bar-label">Maximum</span>
                                        <div className="rp-bar-track"><div className="rp-bar-fill red" style={{ width: '100%' }}></div></div>
                                        <span className="rp-bar-value red">866 <small>ms</small></span>
                                    </div>
                                </div>
                                <div className="rp-card">
                                    <div className="rp-card-title">📊 Percentile Response Times</div>
                                    <div className="rp-pct-row">
                                        <span className="rp-pct-badge blue">P90</span>
                                        <div className="rp-bar-track"><div className="rp-bar-fill blue" style={{ width: '33%' }}></div></div>
                                        <span className="rp-bar-value">288 <small>ms</small></span>
                                    </div>
                                    <div className="rp-pct-row">
                                        <span className="rp-pct-badge yellow">P95</span>
                                        <div className="rp-bar-track"><div className="rp-bar-fill yellow" style={{ width: '53%' }}></div></div>
                                        <span className="rp-bar-value">462 <small>ms</small></span>
                                    </div>
                                    <div className="rp-pct-row">
                                        <span className="rp-pct-badge red">P99</span>
                                        <div className="rp-bar-track"><div className="rp-bar-fill red" style={{ width: '100%' }}></div></div>
                                        <span className="rp-bar-value">866 <small>ms</small></span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── METRICS STRIP ── */}
            <div className="metrics-strip">
                <div className="metrics-inner">
                    <div className="section-eyebrow" style={{ color: 'rgba(255,255,255,.45)' }}>By the numbers</div>
                    <h2>Built for <em>smoothest</em> {' '}<br />performance testing experience.</h2>
                    <div className="metrics-grid">
                        <div className="metric-card">
                            <div className="metric-val">999<span></span></div>
                            <div className="metric-lbl">Max concurrent virtual users</div>
                        </div>
                        <div className="metric-card">
                            <div className="metric-val">4<span></span></div>
                            <div className="metric-lbl">Load test profiles available</div>
                        </div>
                        <div className="metric-card">
                            <div className="metric-val">p99<span></span></div>
                            <div className="metric-lbl">Percentile metrics included</div>
                        </div>
                        <div className="metric-card">
                            <div className="metric-val">60<span>s</span></div>
                            <div className="metric-lbl">From signup to first test result</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── PRICING ── */}
            <section className="lp-section" id="pricing" ref={pricingRef}>
                <div className="section-inner">
                    <div className="section-eyebrow">Pricing</div>
                    <h2>Simple, transparent {' '}<br /><em>credit packs.</em></h2>
                    <p className="section-sub">Buy once, use anytime. 1 Credit = 1 Test Run. Credits never expire.</p>
                    <div className="lp-pricing-grid">
                        <div className="price-card">
                            <div className="plan-name">Starter Pack</div>
                            <div className="price-amount"><span className="sym">$</span>4.99<span className="per">one-time</span></div>
                            <div className="price-desc">Try it out. Perfect for a quick smoke test or first-time user.</div>
                            <div className="price-divider"></div>
                            <ul className="price-features">
                                <li><span className="pf-check">✓</span> 5 Credits</li>
                                <li><span className="pf-check">✓</span> Never Expires</li>
                                <li><span className="pf-check">✓</span> Full Platform Access</li>
                            </ul>
                            <button className="price-btn" onClick={() => { trackPricingBuyClick('pack_starter', 'Starter Pack', 4.99, 5, 'landing'); handleCheckout('pack_starter'); }} disabled={!!checkoutLoading}>
                                {checkoutLoading === 'pack_starter' ? 'Loading...' : 'Buy Now →'}
                            </button>
                        </div>
                        <div className="price-card">
                            <div className="plan-name">Builder Pack</div>
                            <div className="price-amount"><span className="sym">$</span>19.99<span className="per">one-time</span></div>
                            <div className="price-desc">Great for indie devs who test periodically throughout a project.</div>
                            <div className="price-divider"></div>
                            <ul className="price-features">
                                <li><span className="pf-check">✓</span> 20 Credits</li>
                                <li><span className="pf-check">✓</span> Never Expires</li>
                                <li><span className="pf-check">✓</span> Full Platform Access</li>
                            </ul>
                            <button className="price-btn" onClick={() => { trackPricingBuyClick('pack_builder', 'Builder Pack', 19.99, 20, 'landing'); handleCheckout('pack_builder'); }} disabled={!!checkoutLoading}>
                                {checkoutLoading === 'pack_builder' ? 'Loading...' : 'Buy Now →'}
                            </button>
                        </div>
                        <div className="price-card featured">
                            <div className="popular-badge">Best Value</div>
                            <div className="plan-name">Pro Pack</div>
                            <div className="price-amount"><span className="sym">$</span>49.99<span className="per">one-time</span></div>
                            <div className="price-desc">The sweet spot for serious developers and freelancers.</div>
                            <div className="price-divider"></div>
                            <ul className="price-features">
                                <li><span className="pf-check">✓</span> 50 Credits</li>
                                <li><span className="pf-check">✓</span> Never Expires</li>
                                <li><span className="pf-check">✓</span> Full Platform Access</li>
                            </ul>
                            <button className="price-btn" onClick={() => { trackPricingBuyClick('pack_pro', 'Pro Pack', 49.99, 50, 'landing'); handleCheckout('pack_pro'); }} disabled={!!checkoutLoading}>
                                {checkoutLoading === 'pack_pro' ? 'Loading...' : 'Buy Now →'}
                            </button>
                        </div>
                        <div className="price-card">
                            <div className="plan-name">Team Pack</div>
                            <div className="price-amount"><span className="sym">$</span>199.99<span className="per">one-time</span></div>
                            <div className="price-desc">Built for teams and power users with heavy testing needs.</div>
                            <div className="price-divider"></div>
                            <ul className="price-features">
                                <li><span className="pf-check">✓</span> 200 Credits</li>
                                <li><span className="pf-check">✓</span> Never Expires</li>
                                <li><span className="pf-check">✓</span> Full Platform Access</li>
                            </ul>
                            <button className="price-btn" onClick={() => { trackPricingBuyClick('pack_team', 'Team Pack', 199.99, 200, 'landing'); handleCheckout('pack_team'); }} disabled={!!checkoutLoading}>
                                {checkoutLoading === 'pack_team' ? 'Loading...' : 'Buy Now →'}
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── WHY CHOOSE US ── */}
            <section className="lp-section" ref={comparisonRef} style={{ background: 'var(--surface)' }}>
                <div className="section-inner">
                    <div className="section-eyebrow">Why switch</div>
                    <h2>Why teams switch to {' '}<br /><em>APILoadTest.</em></h2>
                    <p className="section-sub">Focus on what matters: finding performance issues. Not wrestling with tools.</p>
                    <div className="comparison-grid">
                        <div className="comparison-card">
                            <div className="comparison-vs">vs JMeter</div>
                            <h3>No XML. No Scripting.</h3>
                            <p>JMeter requires complex test plans, plugins, and XML configuration. With APILoadTest, paste a cURL and run — that's it.</p>
                            <ul className="comparison-list">
                                <li><span className="comparison-check">✓</span> Zero learning curve</li>
                                <li><span className="comparison-check">✓</span> No local installation</li>
                                <li><span className="comparison-check">✓</span> Results in under 60 seconds</li>
                            </ul>
                        </div>
                        <div className="comparison-card">
                            <div className="comparison-vs">vs k6 / Gatling</div>
                            <h3>No Code Required</h3>
                            <p>k6 requires JavaScript. Gatling requires Scala. APILoadTest works with what you already have — cURL or Postman.</p>
                            <ul className="comparison-list">
                                <li><span className="comparison-check">✓</span> Postman collection import</li>
                                <li><span className="comparison-check">✓</span> cURL command import</li>
                                <li><span className="comparison-check">✓</span> Visual load profile selection</li>
                            </ul>
                        </div>
                        <div className="comparison-card">
                            <div className="comparison-vs">vs Cloud Solutions</div>
                            <h3>No Infrastructure Hassle</h3>
                            <p>No Docker. No CLI. No agents. No worker nodes to manage. Just sign up and start testing.</p>
                            <ul className="comparison-list">
                                <li><span className="comparison-check">✓</span> No setup required</li>
                                <li><span className="comparison-check">✓</span> Pay only for the tests you run</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── FAQ ── */}
            <section className="lp-section faq-section" id="faq">
                <div className="section-inner">
                    <div className="faq-layout">
                        <div className="faq-left">
                            <h2>Frequently {' '}<br />asked questions</h2>
                            <p className="faq-left-sub">Answers to the most common questions about API load testing with ALT.</p>
                        </div>
                        <div className="faq-right">
                            {faqData.map((faq, index) => (
                                <div
                                    className={`faq-item ${openFaq === index ? 'faq-item--open' : ''}`}
                                    key={index}
                                >
                                    <button
                                        className="faq-question"
                                        onClick={() => setOpenFaq(openFaq === index ? null : index)}
                                        aria-expanded={openFaq === index}
                                        id={`faq-question-${index}`}
                                    >
                                        <span>{faq.question}</span>
                                        <span className="faq-chevron">{openFaq === index ? '−' : '+'}</span>
                                    </button>
                                    <div className="faq-answer-wrapper" style={{ maxHeight: openFaq === index ? '300px' : '0' }}>
                                        <p className="faq-answer">{faq.answer}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ── CTA ── */}
            <div className="cta-section" ref={ctaSectionRef}>
                <div className="section-eyebrow" style={{ color: 'rgba(255,255,255,.4)' }}>Get started</div>
                <h2>Find Your API's Breaking {' '}<br />Point in <em>60 Seconds.</em></h2>
                <p>No subscriptions. No complex setup. {' '}<br />Just paste your API and see what happens under load.</p>
                <button className="btn-white" onClick={() => { trackFinalCtaClick(); navigate('/login'); }}>
                    Run Your First Test Now →
                </button>
            </div>

            {/* ── FOOTER ── */}
            <footer className="lp-footer">
                <a className="logo" href="/">
                    <img src="/logo.png" alt="APILoadTest" className="logo-img" />
                    APILoadTest.com
                </a>
                <div className="footer-nav">
                    <a href="#how-it-works" onClick={() => trackFooterLinkClick('how_it_works')}>How It Works</a>
                    <a href="#pricing" onClick={() => trackFooterLinkClick('pricing')}>Pricing</a>
                    <a href="/blog" onClick={() => trackFooterLinkClick('blog')}>Blog</a>
                    <a href="/terms" onClick={() => trackFooterLinkClick('terms')}>Terms</a>
                    <a href="/privacy" onClick={() => trackFooterLinkClick('privacy_policy')}>Privacy</a>
                    <a href="/refund" onClick={() => trackFooterLinkClick('refund_policy')}>Refund</a>
                    <a href="mailto:admin@apiloadtest.com" onClick={() => trackFooterLinkClick('contact')}>Contact</a>
                </div>
                <div className="footer-badges">
                    <a href="https://startupfa.me/s/apiloadtest?utm_source=www.apiloadtest.com" target="_blank" rel="noopener noreferrer">
                        <img src="https://startupfa.me/badges/featured-badge-small.webp" alt="ALT - APILoadTest - Featured on Startup Fame" width="224" height="36" />
                    </a>
                    <a href="https://twelve.tools" target="_blank" rel="noopener noreferrer">
                        <img src="https://twelve.tools/badge0-light.svg" alt="Featured on Twelve Tools" width="200" height="54" />
                    </a>
                    <a href="https://wired.business" target="_blank" rel="noopener noreferrer">
                        <img src="https://wired.business/badge0-light.svg" alt="Featured on Wired Business" width="200" height="54" />
                    </a>
                </div>
                <div className="footer-copy">© 2026 APILoadTest.com · All rights reserved</div>
            </footer>

        </div>
    );
};

export default LandingPage;
